import { useEffect, useMemo } from 'react';
import { debounce } from 'lodash';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import Workflow from 'models/Workflow';
import {
  completeWorkflowAction,
  invokeWorkflowAction,
  showWorkflow,
  WorkflowActionIntent,
  workflowQueryKeys,
  WorkflowValuesInput,
} from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';

// A write's response (create / value-write / complete / rerun / dismiss) now
// reflects the FINAL settled state — the evaluator runs synchronously, including
// re-run cascades (P1-1) — so the acting user's own change renders straight from
// the response. The interval below is only background polling, to pick up changes
// made by other users (no WebSockets until Phase 2).
// Collapse nudge bursts into one refetch (a fresh referral settles once per automated
// step); the max wait bounds how stale a continuously-busy run can look.
const NUDGE_DEBOUNCE_MS = 400;
const NUDGE_MAX_WAIT_MS = 1500;

// The fallback cadence only (OLIO-2602): the WorkflowChannel nudge is the primary
// freshness signal; this slow poll covers dropped sockets and missed events.
const FALLBACK_POLL_INTERVAL_MS = 60_000;

/**
 * The single data hook for a workflow run (the key WEB-0 architectural ask).
 *
 * All fetching lives here so the rest of the UI is agnostic to *how* fresh data
 * arrives. Today it polls `GET /api/workflows/:id` until the run is terminal;
 * swapping to a WebSocket subscription in Phase 2 is a one-file change — only the
 * `useQuery` block below changes, the returned shape stays the same.
 */
export default function useWorkflow(id: string | undefined) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const query = useQuery({
    queryKey: workflowQueryKeys.show(id ?? ''),
    queryFn: ({ signal }) => showWorkflow(id!, signal),
    enabled: !!id,
    // --- swap point: replace this background polling with a subscription in Phase 2 ---
    // Background-only: the acting user's writes already return settled state; this
    // catches other users' changes while the run is live, stopping once terminal.
    refetchInterval: (q) => (q.state.data && q.state.data.isTerminal ? false : FALLBACK_POLL_INTERVAL_MS),
    refetchIntervalInBackground: true,
  });

  // Live updates (OLIO-2602): the API broadcasts an id-only nudge whenever this run
  // settles — respond by refetching through the same query, so the fetch stays the
  // single source of truth. Reconnects converge via the fallback poll above.
  // Debounced: a busy referral settles many times in quick succession (every automated
  // step completing is an evaluation), so a burst of nudges collapses into one refetch
  // after a quiet moment — with a max wait so a steady stream can't starve freshness.
  const nudged = useMemo(
    () =>
      debounce(
        (workflowId: string) => queryClient.invalidateQueries({ queryKey: workflowQueryKeys.show(workflowId) }),
        NUDGE_DEBOUNCE_MS,
        { maxWait: NUDGE_MAX_WAIT_MS }
      ),
    [queryClient]
  );
  useEffect(() => () => nudged.cancel(), [nudged]);

  useWebsocketSubscription({
    channel: 'WorkflowChannel',
    params: { workflow_id: id },
    enabled: !!id,
    onReceived: () => nudged(id!),
  });

  const onWriteSuccess = (workflow: Workflow) => {
    if (id) queryClient.setQueryData(workflowQueryKeys.show(id), workflow);
  };

  const onWriteError = () => addToast({ text: 'Something went wrong. Please try again' });

  const actionMutation = useMutation({
    mutationFn: ({ actionId, outputs }: { actionId: string; outputs: WorkflowValuesInput }) =>
      completeWorkflowAction({ workflowId: id!, actionId, outputs }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  const invokeMutation = useMutation({
    mutationFn: ({ actionId, invokeAction }: { actionId: string; invokeAction: WorkflowActionIntent }) =>
      invokeWorkflowAction({ workflowId: id!, actionId, invokeAction }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  return {
    workflow: query.data,
    isLoading: query.isPending && !!id,
    isError: query.isError,
    // True while a poll or write is in flight — drives a subtle "updating" affordance.
    isFetching: query.isFetching,
    refetch: query.refetch,
    completeAction: actionMutation.mutate,
    isCompletingAction: actionMutation.isPending,
    invokeAction: invokeMutation.mutate,
    isInvokingAction: invokeMutation.isPending,
  };
}
